import React from "react";
import { PageHero, SectionHeading } from "../components/Sections";
import { Card } from "../components/ui/card";
import { LeadForm } from "../components/LeadForm";
import { Gauge, Info, CheckCircle2, ShieldCheck } from "lucide-react";

const bands = [
  { range: "300–579", label: "Poor", color: "#e11d48", w: "20%" },
  { range: "580–669", label: "Fair", color: "#f59e0b", w: "40%" },
  { range: "670–739", label: "Good", color: "#eab308", w: "60%" },
  { range: "740–799", label: "Very Good", color: "#65a30d", w: "80%" },
  { range: "800–900", label: "Excellent", color: "#0d9488", w: "100%" },
];

const points = [
  "We do not act as a credit bureau. Reports are provided by the relevant credit information company (bureau).",
  "We obtain your explicit consent before any credit information is requested on your behalf.",
  "We collect only need-based information for the stated purpose.",
  "Your credit information is handled in line with our Privacy Policy and applicable regulations.",
];

export default function CreditScore() {
  return (
    <div>
      <PageHero
        title="Understand Your Credit Score & Report"
        subtitle="Good credit is important. Learn how your score works and check it responsibly, with your consent."
        crumbs={[{ label: "Credit Score & Report" }]}
      />

      <section className="section-pad">
        <div className="container-page grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-primary">
                <Gauge className="h-7 w-7" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold">What is a credit score?</h2>
                <p className="mt-2 text-muted-foreground">
                  A credit score is a number (typically 300–900) that reflects your creditworthiness based on your
                  borrowing and repayment history. Lenders may consider it when assessing applications.
                </p>
              </div>
            </div>

            <Card className="border p-6">
              <h3 className="font-heading text-lg font-semibold">Score ranges</h3>
              <div className="mt-5 space-y-4">
                {bands.map((b) => (
                  <div key={b.range}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{b.label}</span>
                      <span className="text-muted-foreground">{b.range}</span>
                    </div>
                    <div className="mt-1.5 h-2.5 w-full rounded-full bg-secondary">
                      <div className="h-2.5 rounded-full" style={{ width: b.w, backgroundColor: b.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div>
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" /> How we handle your credit information
              </h3>
              <div className="mt-4 space-y-3">
                {points.map((pt) => (
                  <div key={pt} className="flex items-start gap-2.5 rounded-lg border bg-card p-3.5">
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                    <span className="text-sm text-muted-foreground">{pt}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-secondary/50 p-4">
              <Info className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                Loansupportindia does not imply that it is itself a credit bureau. Your credit report is provided by the
                relevant bureau, and access requires your consent.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <Card className="sticky top-24 border p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold">Check your credit health</h3>
              <p className="mt-1 text-sm text-muted-foreground">Submit your details to get assistance.</p>
              <div className="mt-4"><LeadForm compact defaultType="Credit Score & Report" /></div>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
}
